// racketspec.js — scroll-pinned 3D-viewer voor "Het racket zelf".
// Zelfde FBX-model als de hero. Terwijl je scrolt kleedt het racket zich stap voor stap aan
// (wit → silver → zwarte grip → omlijning → logo → naam) en verschijnen de specs rondom de render.
import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const canvas   = document.getElementById('rkt-canvas');
const section  = document.getElementById('rkx');
const rail     = document.getElementById('rkxRail');
const callouts = [...document.querySelectorAll('#rkx .rkco')].sort((a,b)=>(+a.dataset.i)-(+b.dataset.i));
const railDots = rail ? [...rail.querySelectorAll('i')] : [];
const clamp = (v,a,b)=>Math.max(a,Math.min(b,v));

if (canvas) { try { boot(); } catch (e) { console.warn('rkt 3D unavailable:', e && e.message); fallback(); } }

function assetURL(p){
  const base = p.split('/').pop().split('?')[0];
  const rs = (typeof window!=='undefined' && window.__resources) || {};
  const map = (typeof window!=='undefined' && window.RACKETUP_ASSETS) || {};
  return rs[base] || map[base] || p;
}

function fallback(){
  if(section) section.classList.add('ready');
  const st = section && section.querySelector('.rkx-stage');
  if(st){
    const img = document.createElement('img');
    img.src = 'https://racketup.nl/cdn/shop/files/padel_zone_racket.png?v=1756482648&width=900';
    img.alt = 'RacketUp padelracket';
    img.style.cssText = 'position:absolute;top:56%;left:50%;transform:translate(-50%,-50%);max-height:56%;max-width:40%;object-fit:contain;filter:drop-shadow(0 26px 40px rgba(0,0,0,.4));z-index:2';
    st.appendChild(img);
  }
  callouts.forEach((el,i)=> setTimeout(()=>el.classList.add('in'), 200+i*160));
}

function boot(){
  const renderer = new THREE.WebGLRenderer({ canvas, antialias:true, alpha:true, powerPreference:'high-performance' });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 4000);
  camera.position.set(0, 0, 560);

  const keyL = new THREE.DirectionalLight(0xffffff, 2.1); keyL.position.set(3, 6, 6); scene.add(keyL);
  const fillL = new THREE.DirectionalLight(0xffffff, 0.7); fillL.position.set(-5, -2, 4); scene.add(fillL);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x6b7280, 0.6));

  const pivot = new THREE.Group(); scene.add(pivot);
  const spinner = new THREE.Group(); pivot.add(spinner);

  // ---- dressing state ----
  const gripMats=[], bandMats=[];
  let faceTex=null, fctx=null, faceMat=null, CW=720, CH=1160;
  let curStage=-1, fitR=0;
  const logoImg = new Image(); logoImg.crossOrigin='anonymous'; logoImg.src = assetURL('assets/racketup-logo.png');
  logoImg.onload = ()=>{ if(curStage>=4) dress(curStage, true); };
  const irmaImg = new Image(); irmaImg.crossOrigin='anonymous'; irmaImg.src = assetURL('assets/design-irma.png');
  irmaImg.onload = ()=>{ if(curStage>=3) dress(curStage, true); };

  const FBX_URL = (window.__resources && window.__resources.racketFbx) || assetURL('assets/racket.fbx');
  new FBXLoader().load(FBX_URL, (obj)=>{ try{ process(obj); }catch(e){ console.warn(e); fallback(); } }, null, ()=>fallback());

  function process(obj){
    // strip baked shadow meshes
    const rm=[]; obj.traverse(n=>{ if(n.isMesh){ const ms=Array.isArray(n.material)?n.material:[n.material]; if(ms.some(m=>m&&/shadow/i.test(m.name))) rm.push(n); } });
    rm.forEach(n=>n.parent&&n.parent.remove(n));

    // face paint texture (planar-projected onto real geometry)
    const fc=document.createElement('canvas'); fc.width=CW; fc.height=CH; fctx=fc.getContext('2d');
    faceTex=new THREE.CanvasTexture(fc); faceTex.colorSpace=THREE.SRGBColorSpace; faceTex.anisotropy=8;
    faceMat=new THREE.MeshPhysicalMaterial({ map:faceTex, roughness:.34, metalness:0, clearcoat:1, clearcoatRoughness:.09, envMapIntensity:1.2 });

    const faceMeshes=[];
    obj.traverse(n=>{ if(!n.isMesh) return;
      const nm=(n.name||'').toLowerCase();
      const om=(Array.isArray(n.material)?n.material:[n.material]).map(m=>(m&&m.name)||'').join(',').toLowerCase();
      let mat;
      if(/foam|hole/.test(nm)||/foamhole/.test(om)){ mat=new THREE.MeshStandardMaterial({color:0x0c0f08,roughness:.5,metalness:.2}); }
      else if(/rubber|grip/.test(nm)){ mat=new THREE.MeshPhysicalMaterial({color:0xEDEFF3,roughness:.55,metalness:.05}); gripMats.push(mat); }
      else if(/wristband|strap/.test(nm)){ mat=new THREE.MeshPhysicalMaterial({color:0xEDEFF3,roughness:.6,metalness:.05}); bandMats.push(mat); }
      else if(/bottomlogo/.test(nm)||/bottomlogo/.test(om)){ mat=new THREE.MeshStandardMaterial({color:0xC7CBD3,roughness:.4,metalness:.3}); }
      else { mat=faceMat; faceMeshes.push(n); }
      n.material=mat;
    });

    obj.updateMatrixWorld(true);
    let box=new THREE.Box3().setFromObject(obj); const ctr=box.getCenter(new THREE.Vector3()); obj.position.sub(ctr); obj.updateMatrixWorld(true);

    // planar projection → per-vertex UVs
    const _v=new THREE.Vector3(); let mnx=1e9,mxx=-1e9,mny=1e9,mxy=-1e9;
    faceMeshes.forEach(m=>{ const p=m.geometry.attributes.position; for(let i=0;i<p.count;i++){ _v.set(p.getX(i),p.getY(i),p.getZ(i)).applyMatrix4(m.matrixWorld); if(_v.x<mnx)mnx=_v.x; if(_v.x>mxx)mxx=_v.x; if(_v.y<mny)mny=_v.y; if(_v.y>mxy)mxy=_v.y; } });
    const bw=mxx-mnx, bh=mxy-mny;
    faceMeshes.forEach(m=>{ const p=m.geometry.attributes.position; const uv=new Float32Array(p.count*2); for(let i=0;i<p.count;i++){ _v.set(p.getX(i),p.getY(i),p.getZ(i)).applyMatrix4(m.matrixWorld); uv[i*2]=(_v.x-mnx)/bw; uv[i*2+1]=(_v.y-mny)/bh; } m.geometry.setAttribute('uv', new THREE.BufferAttribute(uv,2)); });
    CH=1160; CW=Math.max(360, Math.round(CH*bw/bh)); fc.width=CW; fc.height=CH;

    const size=box.getSize(new THREE.Vector3());
    const maxDim=Math.max(size.x,size.y,size.z)||1;
    const s=360/maxDim;
    pivot.scale.setScalar(s);
    pivot.rotation.x=-0.10;
    spinner.add(obj);
    pivot.updateMatrixWorld(true);

    // bounding radius in scaled units (computed from the model's own box → no stale-matrix issues)
    const sphere0=new THREE.Sphere(); box.getBoundingSphere(sphere0);
    fitR = sphere0.radius * s;

    dress(0, true);
    canvas.classList.add('ready');
    if(section) section.classList.add('ready');
    resize();
  }

  // ---- progressive dressing ----
  function drawHeadEllipse(inset){
    // teardrop head sits in the upper ~78% of the projected face
    const cx=CW/2, cy=CH*0.40, rx=CW*0.40-inset, ry=CH*0.34-inset;
    fctx.save(); fctx.beginPath(); fctx.ellipse(cx,cy,rx,ry,0,0,Math.PI*2); fctx.restore();
    return {cx,cy,rx,ry};
  }
  function dress(stage, force){
    if(!fctx) return;
    if(stage===curStage && !force) return; curStage=stage;
    const frameCol = stage>=5 ? '#0B3D2E' : (stage>=1 ? '#C7CBD3' : '#FBFCF5');  // wit → silver → donkergroen (laatste)
    const gripCol  = stage>=2 ? 0x14161d : 0xEDEFF3;          // wit → zwart
    gripMats.forEach(m=>m.color.set(gripCol));
    bandMats.forEach(m=>m.color.set(gripCol));
    if(faceMat){ faceMat.metalness = stage>=1 ? 0.35 : 0; faceMat.roughness = stage>=1 ? 0.28 : 0.34; faceMat.needsUpdate=true; }

    fctx.globalAlpha=1; fctx.fillStyle=frameCol; fctx.fillRect(0,0,CW,CH);

    if(stage>=3 && irmaImg.complete && irmaImg.naturalWidth){ // echt Irma-design op de kop
      const ar=irmaImg.naturalWidth/irmaImg.naturalHeight;
      const dw=CW*0.97, dh=dw/ar;
      fctx.drawImage(irmaImg, (CW-dw)/2, CH*0.285-dh/2, dw, dh);
    }
    faceTex.needsUpdate=true;
  }

  // ---- drag to rotate ----
  let vel=0, dragging=false, lastX=0, dragUntil=0;
  const onDown=(x)=>{ dragging=true; lastX=x; canvas.style.cursor='grabbing'; };
  const onMove=(x)=>{ if(!dragging) return; const dx=x-lastX; lastX=x; vel=dx*0.01; spinner.rotation.y+=vel; dragUntil=performance.now()+1500; };
  const onUp=()=>{ dragging=false; canvas.style.cursor='grab'; };
  canvas.addEventListener('mousedown', e=>onDown(e.clientX));
  addEventListener('mousemove', e=>onMove(e.clientX));
  addEventListener('mouseup', onUp);
  canvas.addEventListener('touchstart', e=>onDown(e.touches[0].clientX), {passive:true});
  canvas.addEventListener('touchmove', e=>onMove(e.touches[0].clientX), {passive:true});
  canvas.addEventListener('touchend', onUp);

  function resize(){ const r=canvas.getBoundingClientRect(); const w=Math.max(1,r.width), h=Math.max(1,r.height); renderer.setSize(w,h,false); camera.aspect=w/h; camera.updateProjectionMatrix();
    if(fitR){ // auto-fit: pull camera back so the full racket fits both axes with margin
      const vF=camera.fov*Math.PI/180;
      const distV=fitR/Math.sin(vF/2);
      const hF=2*Math.atan(Math.tan(vF/2)*camera.aspect);
      const distH=fitR/Math.sin(hF/2);
      camera.position.z=Math.max(distV,distH)*0.66;
      camera.updateProjectionMatrix();
    }
  }
  const ro=new ResizeObserver(resize); ro.observe(canvas); addEventListener('resize', resize);

  let pinned = true;
  function computeProgress(){
    if(!section) return 0;
    const r=section.getBoundingClientRect();
    if(!pinned){ return clamp((innerHeight - r.top)/(r.height+innerHeight), 0, 1); }
    const span=r.height-innerHeight; return clamp(-r.top/(span>0?span:1), 0, 1);
  }
  function applyReveals(p){
    const n=callouts.length;
    const shown=Math.floor((p/0.6)*n + 0.0001);
    let active=-1;
    callouts.forEach((el,i)=>{ if(i<shown){ el.classList.add('in'); active=i; } else el.classList.remove('in'); });
    railDots.forEach((d,i)=> d.classList.toggle('on', i<=active));
    dress(clamp(active+1, 0, 5));   // each revealed callout dresses one more step
  }

  let visible=true;
  const io=new IntersectionObserver(es=>{ visible=es[0].isIntersecting; }, {threshold:0});
  if(section) io.observe(section);

  let idle=0;
  function frame(){
    requestAnimationFrame(frame);
    if(!visible) return;
    const p=computeProgress();
    applyReveals(p);
    if(dragging || performance.now()<dragUntil){ if(!dragging && Math.abs(vel)>0.0004){ spinner.rotation.y+=vel; vel*=0.94; } }
    else if(pinned){ idle+=0.0016; spinner.rotation.y = p*Math.PI*2.2 + idle; }
    else { idle+=0.006; spinner.rotation.y = idle; }
    renderer.render(scene, camera);
  }
  frame();
}
